import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { clickCollectApi } from '@/lib/api';
import { CheckCircle2, Clock, XCircle, Loader2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(true);
  const attempts = useRef(0);

  useEffect(() => {
    if (!orderId || !sessionId) return;
    const poll = async () => {
      if (attempts.current >= 10) { setPolling(false); return; }
      attempts.current += 1;
      try {
        const res = await clickCollectApi.getOrderStatus(orderId, sessionId);
        setOrder(res.data);
        if (res.data.payment_status === 'paid') {
          setPolling(false);
          return;
        }
        if (res.data.payment_status === 'expired' || res.data.payment_status === 'failed') {
          setPolling(false);
          return;
        }
      } catch (e) { console.warn('Order status poll failed, retrying:', e); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [orderId, sessionId]);

  const isPaid = order?.payment_status === 'paid';
  const isFailed = order?.payment_status === 'expired' || order?.payment_status === 'failed';

  return (
    <div className="min-h-screen bg-cream" data-testid="order-success-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-xl mx-auto px-4 text-center">
          {polling ? (
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-800/5">
              <Loader2 size={48} className="mx-auto text-garden-500 animate-spin mb-4" />
              <h1 className="text-2xl font-heading font-bold text-garden-800 mb-2">Vérification du paiement...</h1>
              <p className="text-charcoal/60">Veuillez patienter quelques instants.</p>
            </div>
          ) : isPaid ? (
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-800/5" data-testid="order-confirmed">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={40} className="text-green-600" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-garden-800 mb-2">Commande confirmée !</h1>
              <p className="text-charcoal/60 mb-6">Merci pour votre commande. Voici le récapitulatif :</p>
              <div className="bg-garden-50 rounded-2xl p-6 text-left mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-xs text-garden-500 font-medium">Commande</span>
                    <p className="font-heading font-bold text-garden-800 text-lg">{order.id}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-garden-500 font-medium">Total</span>
                    <p className="font-heading font-bold text-garden-800 text-lg">{order.total?.toFixed(2).replace('.', ',')} €</p>
                  </div>
                </div>
                <div className="border-t border-garden-200 pt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="text-garden-500" />
                    <span className="text-charcoal">Retrait le <strong>{order.pickup_date}</strong> à <strong>{order.pickup_time}</strong></span>
                  </div>
                  <p className="text-xs text-charcoal/50 ml-6">198 Cours de l'Argonne, 33000 Bordeaux</p>
                </div>
                {order.items && (
                  <div className="border-t border-garden-200 mt-4 pt-4">
                    <span className="text-xs text-garden-500 font-medium block mb-2">Articles</span>
                    {order.items.map((item) => (
                      <div key={`${item.name}-${item.variant_name || 'def'}`} className="flex justify-between text-sm text-charcoal py-1">
                        <span>{item.quantity}x {item.name}{item.variant_name ? ` (${item.variant_name})` : ''}</span>
                        <span>{(item.price * item.quantity).toFixed(2).replace('.', ',')} €</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline" className="rounded-full border-garden-200">Retour à l'accueil</Button>
                </Link>
                <Link to="/commander">
                  <Button className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                    <ShoppingBag size={16} className="mr-1" /> Nouvelle commande
                  </Button>
                </Link>
              </div>
            </div>
          ) : isFailed ? (
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-800/5" data-testid="order-failed">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle size={40} className="text-red-600" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-red-700 mb-2">Paiement échoué</h1>
              <p className="text-charcoal/60 mb-6">Le paiement n'a pas pu être traité. Aucun montant n'a été débité.</p>
              <Link to="/commander">
                <Button className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">Réessayer</Button>
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-800/5">
              <Clock size={48} className="mx-auto text-charcoal/30 mb-4" />
              <h1 className="text-2xl font-heading font-bold text-garden-800 mb-2">Statut inconnu</h1>
              <p className="text-charcoal/60">Nous n'avons pas pu vérifier le statut de votre paiement. Contactez-nous si besoin.</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
